import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Caption,
  Heading,
  Icon,
  type IconName,
  Stack,
  Text,
  tv,
  useToast,
} from '@eluve/components';
import { timestampToHumanReadable } from '@eluve/date-utils';

import { FlowStepper } from '../components/FlowStepper';
import { WhiteCard } from '../components/WhiteCard';
import { useImportJob } from '../hooks/useImportJob';
import {
  PATIENT_IMPORT_ROUTE,
  errMsg,
  fetchAllImportRows,
  triggerCsvDownload,
} from '../utils';

import {
  type ImportOutcomeCounts,
  countImportOutcomes,
} from './review-grouping';

type Variant = 'default' | 'partial' | 'cancelled' | 'failed';
type DownloadKind = 'skipped' | 'failed';
type Tone = 'positive' | 'warning' | 'neutral' | 'negative';

const HEADLINE: Record<Variant, { title: string; icon: IconName; tone: Tone }> =
  {
    default: {
      title: 'Import complete',
      icon: 'CircleCheck',
      tone: 'positive',
    },
    partial: {
      title: 'Import partially complete',
      icon: 'TriangleAlert',
      tone: 'warning',
    },
    cancelled: {
      title: 'Import cancelled',
      icon: 'CircleStop',
      tone: 'neutral',
    },
    failed: { title: 'Import failed', icon: 'CircleX', tone: 'negative' },
  };

const toneRingStyles = tv({
  base: 'flex size-14 items-center justify-center rounded-full',
  variants: {
    tone: {
      positive: 'bg-bg-positive-subtle text-text-positive-subtle',
      warning: 'bg-bg-warning-subtle text-text-warning-subtle',
      neutral: 'bg-bg-tertiary text-text-tertiary',
      negative: 'bg-bg-negative-subtle text-text-negative-subtle',
    },
  },
});

const statTileStyles = tv({
  base: 'min-w-32 flex-1 rounded-lg border p-4 text-center',
  variants: {
    tone: {
      positive:
        'border-border-positive-subtle bg-bg-positive-subtle text-text-positive-subtle',
      warning:
        'border-border-warning-subtle bg-bg-warning-subtle text-text-warning-subtle',
      neutral: 'border-border-tertiary bg-bg-secondary text-text-tertiary',
      negative:
        'border-border-negative-subtle bg-bg-negative-subtle text-text-negative-subtle',
    },
  },
});

const StatTile: React.FC<{
  tone: Tone;
  label: string;
  count: number;
}> = ({ tone, label, count }) => (
  <div className={statTileStyles({ tone })}>
    <Stack gap={1} align="center">
      <Caption>{label}</Caption>
      <Heading size="3xl" weight="semibold">
        {count}
      </Heading>
    </Stack>
  </div>
);

export const PatientImportDoneScreen: React.FC<{
  variant: Variant;
  jobId: string;
  tenantId: string;
}> = ({ variant, jobId, tenantId }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const job = useImportJob(tenantId, jobId);
  const [downloading, setDownloading] = useState<DownloadKind | null>(null);
  const [outcomeCounts, setOutcomeCounts] =
    useState<ImportOutcomeCounts | null>(null);
  const headline = HEADLINE[variant];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchAllImportRows(apiClient, tenantId, jobId);
        if (cancelled) return;
        setOutcomeCounts(countImportOutcomes(result.rows));
      } catch (err) {
        if (cancelled) return;
        toast.error(`Failed to load committed rows: ${errMsg(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId, jobId, job.committedCount, toast]);

  const createdCount = outcomeCounts?.created ?? 0;
  const updatedCount = outcomeCounts?.updated ?? 0;
  const unchangedCount = outcomeCounts?.unchanged ?? 0;
  const skippedCount = outcomeCounts?.skipped ?? job.skippedCount;
  const importedCount = createdCount + updatedCount;
  const headlineText =
    outcomeCounts !== null && importedCount === 0 && unchangedCount > 0
      ? 'No patient changes'
      : `${job.committedCount} patients imported`;
  const hasSkipped = skippedCount > 0;
  const hasFailed = job.failedCount > 0;

  const finishedAt = useMemo(() => {
    const ts = job.finalizedAt ?? job.cancelledAt ?? job.updatedAt;
    return ts ? timestampToHumanReadable(new Date(ts).getTime()) : '';
  }, [job.finalizedAt, job.cancelledAt, job.updatedAt]);

  const onDownload = async (kind: DownloadKind) => {
    setDownloading(kind);
    try {
      const res =
        kind === 'skipped'
          ? await apiClient.patientImport.downloadSkippedCsv({
              params: { tenantId, jobId },
            })
          : await apiClient.patientImport.downloadFailedCsv({
              params: { tenantId, jobId },
            });
      if (res.status !== 200) {
        throw new Error(`Download failed (${res.status})`);
      }
      triggerCsvDownload(res.body, `${kind}.csv`);
    } catch (err) {
      toast.error(`Failed to download ${kind}.csv: ${errMsg(err)}`);
    } finally {
      setDownloading(null);
    }
  };

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack direction="horizontal" align="start" justify="between" gap={4}>
        <Stack gap={1}>
          <Heading size="3xl" weight="semibold">
            {headline.title}
          </Heading>
          <Stack direction="horizontal" gap={2} align="center">
            <Icon name="FileText" size="xs" />
            <Text inline color="tertiary" size="sm" className="font-mono">
              {job.filename}
            </Text>
            {finishedAt ? (
              <Text inline color="tertiary" size="sm">
                · finished {finishedAt}
              </Text>
            ) : null}
          </Stack>
        </Stack>
        <Link to={`${PATIENT_IMPORT_ROUTE}/new`}>
          <Button variant="outline">
            <Icon name="Upload" size="auto" />
            Import another file
          </Button>
        </Link>
      </Stack>

      <FlowStepper current="importing" />

      {variant === 'failed' && (job.failureCategory || job.failureMessage) ? (
        <Alert variant="destructive">
          <Icon name="AlertOctagon" />
          <AlertTitle>{job.failureCategory ?? 'Failure'}</AlertTitle>
          <AlertDescription>
            {job.failureMessage ?? 'No further details available.'}
          </AlertDescription>
        </Alert>
      ) : null}

      <WhiteCard>
        <Stack gap={6} align="center" className="py-6">
          <div className={toneRingStyles({ tone: headline.tone })}>
            <Icon name={headline.icon} size="xl" />
          </div>

          <Stack gap={1} align="center">
            <Heading size="3xl" weight="semibold">
              {headlineText}
            </Heading>
            <Text color="tertiary" size="sm">
              {outcomeCounts === null ? '—' : createdCount} new patients created
              · {outcomeCounts === null ? '—' : updatedCount} existing records
              updated · {outcomeCounts === null ? '—' : unchangedCount} already
              imported · {skippedCount} rows skipped
            </Text>
          </Stack>

          <Stack
            direction="horizontal"
            gap={3}
            align="stretch"
            className="w-full max-w-2xl"
          >
            <StatTile tone="positive" label="CREATED" count={createdCount} />
            <StatTile tone="warning" label="UPDATED" count={updatedCount} />
            <StatTile tone="neutral" label="UNCHANGED" count={unchangedCount} />
            <StatTile tone="neutral" label="SKIPPED" count={skippedCount} />
          </Stack>

          <Stack direction="horizontal" gap={2}>
            {hasSkipped ? (
              <Button
                variant="outline"
                onClick={() => onDownload('skipped')}
                disabled={downloading !== null}
              >
                <Icon name="Download" size="auto" />
                Download skipped.csv
              </Button>
            ) : null}
            {hasFailed ? (
              <Button
                variant="outline"
                onClick={() => onDownload('failed')}
                disabled={downloading !== null}
              >
                <Icon name="Download" size="auto" />
                Download failed.csv
              </Button>
            ) : null}
            <Link to={PATIENT_IMPORT_ROUTE}>
              <Button>Back to imports</Button>
            </Link>
          </Stack>
        </Stack>
      </WhiteCard>
    </Stack>
  );
};
